import React, { FC, Fragment, ReactNode } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useSelector } from 'react-redux';

import { IAccrualDetailedBalanceDTO, IAccrualSummaryBalanceDTO } from 'src/api/dtos';
import { EmptyState } from 'src/shared/components';
import { useTranslate } from 'src/shared/providers';
import { formatInputDuration } from 'src/shared/utils';
import { getDurationFormat } from 'src/store/user/user.selectors';

const ACCRUAL_SUMMARY_CARD_HEIGHT = 88;
const MAX_HEIGHT = 400;

interface Props {
  selectedBenefitId: IAccrualDetailedBalanceDTO['benefitId'] | undefined;
  accruals: IAccrualSummaryBalanceDTO['accruals'] | undefined;
  onSelect: (accrual: IAccrualDetailedBalanceDTO) => void;
  loading?: boolean;
}

export const AccrualSummarySection: FC<Props> = ({ selectedBenefitId, accruals, onSelect, loading }) => {
  const { t } = useTranslate();
  const theme = useTheme();
  const durationFormat = useSelector(getDurationFormat);

  if (loading) {
    return (
      <Wrapper title={t('accrualSummary.balances')}>
        <Skeletons />
      </Wrapper>
    );
  }

  if (!accruals?.length) {
    return (
      <Wrapper title={t('accrualSummary.balances')}>
        <EmptyState
          sx={{ height: '100%', paddingX: '1.5rem', margin: '3rem 1rem' }}
          title={t('global.nothingToShow')}
          size="small"
        />
      </Wrapper>
    );
  }

  return (
    <Wrapper title={t('accrualSummary.balances')}>
      {accruals.map((accrual, index) => {
        const selected = selectedBenefitId === accrual.benefitId;

        const formattedAmountInSeconds = accrual.amountInSeconds
          ? `${formatInputDuration(accrual.amountInSeconds, durationFormat, 'seconds')} h`
          : '0 h';

        const formattedAmount =
          accrual.amountInDays === null
            ? formattedAmountInSeconds
            : `${formattedAmountInSeconds} / ${accrual.amountInDays} ${t('global.dayS')}`;

        return (
          <Fragment key={`accrual-summary-id-${index}`}>
            <Paper
              sx={{
                backgroundColor: !selected ? theme.palette.background.paper : theme.palette.primary.main,
                color: !selected ? theme.palette.text.primary : theme.palette.primary.contrastText,
                paddingX: '1rem',
                paddingY: '0.75rem',
                height: ACCRUAL_SUMMARY_CARD_HEIGHT,
                cursor: 'pointer',
              }}
              onClick={() => onSelect(accrual)}
            >
              <Typography
                fontWeight={400}
                className={!selected ? 'text-muted' : undefined}
                color={!selected ? undefined : theme.palette.primary.contrastText}
                variant="body2"
              >
                {accrual.benefitName}
              </Typography>

              <Box marginTop={2}>
                <Typography fontWeight={600} variant="body2">
                  {formattedAmount}
                </Typography>
              </Box>
            </Paper>
          </Fragment>
        );
      })}
    </Wrapper>
  );
};

const Wrapper: FC<{ title: string; children: ReactNode }> = ({ title, children }) => {
  return (
    <Stack direction="column" spacing={2}>
      <Typography fontWeight={600} variant="body1">
        {title}
      </Typography>
      <Paper elevation={4} sx={{ padding: '1.5rem', height: MAX_HEIGHT, maxHeight: MAX_HEIGHT, overflowY: 'auto' }}>
        <Stack direction="column" justifyContent="center" spacing={2} width="100%">
          {children}
        </Stack>
      </Paper>
    </Stack>
  );
};

const Skeletons: FC = () => {
  return (
    <>
      <Skeleton sx={{ height: ACCRUAL_SUMMARY_CARD_HEIGHT, borderRadius: 1 }} variant="rectangular" animation="wave" />
      <Skeleton sx={{ height: ACCRUAL_SUMMARY_CARD_HEIGHT, borderRadius: 1 }} variant="rectangular" animation="wave" />
      <Skeleton sx={{ height: ACCRUAL_SUMMARY_CARD_HEIGHT, borderRadius: 1 }} variant="rectangular" animation="wave" />
    </>
  );
};
